import {
  OwnerId,
  FactClusterId,
  ResolutionId,
  FactSearchArgs,
  FactId,
  ScopedLibrarySectionId,
  CountResolutionsArgs,
  ListResolutionsArgs,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function search(args: FactSearchArgs) {
  return await platformedClient.internalRequest("post", `/region/{region}/api/vendor_toolkit/facts/search`, {
    body: args,
  });
}

export async function getCounts() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/facts/counts`, {});
}

export async function updateDisabled(factId: FactId, disabled: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/facts/{fact_id}/disabled`,
    {path: {fact_id: factId}, body: disabled},
  );
  await invalidateQueries([
    q.vendorToolkit.facts.filter(),
    q.vendorToolkit.factCounts.filter(),
    q.vendorToolkit.documentFacts.filter(),
    q.vendorToolkit.questionUsedFacts.filter(),
    q.vendorToolkit.questionGeneratedFacts.filter(),
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolution.filter(),
  ]);
  return res;
}

export async function updateLibrarySection(factId: FactId, scopedId: ScopedLibrarySectionId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/facts/{fact_id}/library_section`,
    {path: {fact_id: factId}, body: scopedId},
  );
  await invalidateQueries([
    q.vendorToolkit.facts.filter(),
    q.vendorToolkit.factCounts.filter(),
    q.vendorToolkit.documentFacts.filter(),
    q.vendorToolkit.questionUsedFacts.filter(),
    q.vendorToolkit.questionGeneratedFacts.filter(),
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(),
  ]);
  return res;
}

export async function listTopLevelClusters() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/fact_clusters`, {});
}

export async function listEquivalences(clusterId: FactClusterId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/fact_clusters/{cluster_id}/equivalences`,
    {path: {cluster_id: clusterId}},
  );
}

export async function listResolutions(args: ListResolutionsArgs) {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/resolutions`, {
    query: args,
  });
}

export async function countResolutions(args: CountResolutionsArgs) {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/resolutions/count`, {
    query: args,
  });
}

export async function listResolvedResolutions() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/resolved_resolutions`, {});
}

export async function listIgnoredResolutions() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/ignored_resolutions`, {});
}

export async function getResolution(resolutionId: ResolutionId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}`,
    {path: {resolution_id: resolutionId}},
  );
}

export async function resolveResolution(resolutionId: ResolutionId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}/resolve`,
    {path: {resolution_id: resolutionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolutionsCount.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(resolutionId),
  ]);
  return res;
}

export async function ignoreResolution(resolutionId: ResolutionId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}/ignore`,
    {path: {resolution_id: resolutionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolutionsCount.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(resolutionId),
  ]);
  return res;
}

export async function revertResolution(resolutionId: ResolutionId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}/revert`,
    {path: {resolution_id: resolutionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolutionsCount.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(resolutionId),
  ]);
  return res;
}

export async function assignResolution(resolutionId: ResolutionId, ownerId: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}/assign`,
    {path: {resolution_id: resolutionId}, body: ownerId},
  );
  await invalidateQueries([
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(resolutionId),
  ]);
  return res;
}

export async function updateResolutionIsInternal(resolutionId: ResolutionId, isInternal: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/resolutions/{resolution_id}/is_internal`,
    {path: {resolution_id: resolutionId}, body: isInternal},
  );
  await invalidateQueries([
    q.vendorToolkit.resolutions.filter(),
    q.vendorToolkit.resolutionsCount.filter(),
    q.vendorToolkit.resolvedResolutions.filter(),
    q.vendorToolkit.ignoredResolutions.filter(),
    q.vendorToolkit.resolution.filter(resolutionId),
  ]);
  return res;
}
